import { Plus, Check } from "lucide-react";
import { useState } from "react";
import { MENU_ITEMS } from "../data/menu";
import { useCart } from "../context/CartContext";

const formatPrice = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(n);

const MenuCard = ({ item, idx }) => {
  const { addItem, openDrawer } = useCart();
  const [justAdded, setJustAdded] = useState(false);

  const handleAdd = () => {
    addItem(item);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1200);
  };

  return (
    <article
      data-testid={`menu-item-${item.id}`}
      className="bg-[#F5EBD3] rounded-xl sm:rounded-2xl p-5 sm:p-6 border border-[#E6DFD3] transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-[#2B241D]/10 fade-up flex flex-col"
      style={{ animationDelay: `${idx * 80}ms` }}
    >
      <div className="relative w-full aspect-[4/3] overflow-hidden rounded-lg mb-6">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        />
        <span className="absolute top-3 left-3 bg-[#F5EBD3]/95 backdrop-blur-sm text-[#2B241D] text-[10px] uppercase tracking-[0.2em] font-medium px-3 py-1.5 rounded-full border border-[#E6DFD3]">
          {item.tag}
        </span>
      </div>

      <div className="flex items-baseline justify-between gap-4 mb-3">
        <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-[#2B241D] leading-tight">
          {item.name}
        </h3>
        <span
          data-testid={`menu-price-${item.id}`}
          className="font-serif text-xl sm:text-2xl text-[#D96C42] whitespace-nowrap"
        >
          {formatPrice(item.price)}
        </span>
      </div>
      <p className="font-sans text-sm text-[#5C5146] leading-relaxed font-light flex-1">
        {item.description}
      </p>

      <div className="mt-6 flex items-center gap-3">
        <button
          data-testid={`add-to-cart-${item.id}`}
          onClick={handleAdd}
          className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
            justAdded
              ? "bg-[#25D366] text-white"
              : "bg-[#2B241D] text-[#F5EBD3] hover:bg-[#D96C42]"
          }`}
        >
          {justAdded ? (
            <>
              <Check size={16} strokeWidth={2} />
              Añadido
            </>
          ) : (
            <>
              <Plus size={16} strokeWidth={2} />
              Añadir al carrito
            </>
          )}
        </button>
        <button
          data-testid={`view-cart-${item.id}`}
          onClick={openDrawer}
          aria-label="Ver carrito"
          className="w-11 h-11 rounded-full border border-[#E6DFD3] bg-[#F4EFE6] hover:bg-[#EAE3D5] flex items-center justify-center text-[#2B241D] transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
            <path d="M3 6h18" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
        </button>
      </div>
    </article>
  );
};

export const Menu = () => {
  const { openDrawer, totalItems } = useCart();

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#EAE3D5]/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 sm:mb-24">
          <div className="md:col-span-7">
            <div className="font-sans text-xs uppercase tracking-[0.3em] text-[#D96C42] mb-6">
              — Combos y especiales
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium text-[#2B241D]">
              Arma tu pedido <em className="italic font-normal">a tu gusto</em>.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="font-sans text-base sm:text-lg text-[#2B241D]/75 leading-relaxed font-light">
              Toca “Añadir al carrito” en lo que quieras. Cuando estés listo,
              enviamos tu pedido por WhatsApp con un solo clic y confirmamos
              disponibilidad y tiempo de entrega.
            </p>
          </div>
        </div>

        {/* Menu grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {MENU_ITEMS.map((item, idx) => (
            <MenuCard key={item.id} item={item} idx={idx} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 sm:mt-20 text-center">
          <p className="font-serif italic text-xl sm:text-2xl text-[#2B241D]/80 mb-6">
            ¿Ya elegiste? Revisa tu pedido y envíalo por WhatsApp.
          </p>
          <button
            data-testid="menu-view-cart"
            onClick={openDrawer}
            className="inline-flex items-center gap-3 bg-[#2B241D] text-[#F5EBD3] px-8 py-4 rounded-full text-sm font-medium hover:bg-[#D96C42] transition-colors"
          >
            Ver mi carrito
            {totalItems > 0 && (
              <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-[#D96C42] text-[#F5EBD3] text-xs font-medium">
                {totalItems}
              </span>
            )}
          </button>
        </div>
      </div>
    </section>
  );
};
